/*
 * Description:
 * ---------------------
 * The firewall's own rules, one direction at a time, laid out the way AWS lays
 * out a security group's rules so they read as familiar: a table to read,
 * with its count, a filter and Edit; and an editor where every rule is a row
 * (a type that fills in the protocol and port, a destination or source as
 * chips under a search box, a description), Add rule, warnings for what is
 * risky, Preview changes and one Save for the lot. Unlike a security group the
 * rules are judged in order, so each has its number and moves up or down, and
 * a last row says what happens to everything they do not match. Wording in
 * firewallWords.js.
 *
 * ! file components/FirewallRuleTable.js
 */
import React, { useState } from "react";
import { FiAlertTriangle, FiArrowDown, FiArrowUp, FiLock, FiPlus, FiSearch } from "react-icons/fi";
import AddressChips from "./AddressChips";
import * as W from "./firewallWords";

const T = W.RULE_TABLE;
const typeOf = (key) => W.RULE_TYPES.find((t) => t.key === key);

let fresh = 0;

/* A rule as the editor holds it: every choice as a row shows it. */
export function draftOf(r) {
  const type = W.ruleType(r);
  return {
    key: r.id || `new-${++fresh}`,
    id: r.id || "",
    action: r.action || "block",
    type: type.key,
    ports: type.custom ? (r.ports || []).map(String).join(", ") : "",
    who: r.group ? `group:${r.group}` : (r.local || []).length ? "local" : "all",
    local: r.local || [],
    where: (r.remote || []).length ? "custom" : "any",
    remote: r.remote || [],
    when: r.schedule ? "scheduled" : "always",
    days: r.schedule?.days || [],
    from: r.schedule?.from || "09:00",
    to: r.schedule?.to || "17:00",
    name: r.name || "",
  };
}

/* A new row: AWS starts one as Custom TCP with the port to fill in; one
   coming in starts from a source to name, since anywhere is rarely right. */
const newDraft = (direction) => ({
  ...draftOf({ action: direction === "in" ? "allow" : "block", protocol: "tcp", ports: [] }),
  type: "custom-tcp",
  where: direction === "in" ? "custom" : "any",
});

/* The ports typed for a custom type, as the box takes them: a number for a
   port, "from-to" for a range; and what could not be read. */
export function portsOf(text) {
  const ports = [];
  const bad = [];
  for (const tok of String(text).split(/[\s,]+/).filter(Boolean)) {
    const m = tok.match(/^(\d{1,5})(?:-(\d{1,5}))?$/);
    const from = m && Number(m[1]);
    const to = m && (m[2] ? Number(m[2]) : from);
    if (!m || from < 1 || to > 65535 || from > to) bad.push(tok);
    else ports.push(from === to ? from : `${from}-${to}`);
  }
  return { ports, bad };
}

/* The rule a row stands for. */
export function ruleOf(d, direction) {
  const type = typeOf(d.type);
  const r = {
    ...(d.id ? { id: d.id } : {}), name: d.name.trim(), action: d.action, direction,
    protocol: type.protocol, ports: type.custom ? portsOf(d.ports).ports : type.ports,
  };
  if (d.where === "custom") r.remote = d.remote;
  if (direction === "out" && d.who.startsWith("group:")) r.group = d.who.slice("group:".length);
  if (direction === "out" && d.who === "local") r.local = d.local;
  if (d.when === "scheduled") r.schedule = { days: d.days, from: d.from, to: d.to };
  return r;
}

/* What is wrong with a row, as sentences; none when nothing is. */
export function rowProblems(d, direction) {
  const out = [];
  const type = typeOf(d.type);
  if (type.custom) {
    const { ports, bad } = portsOf(d.ports);
    if (bad.length) out.push(T.problems.port(bad[0]));
    else if (!ports.length) out.push(T.problems.ports);
  } else if (direction === "in" && d.action === "allow" && !type.ports.length) {
    out.push(T.problems.inPorts);
  }
  if (d.where === "custom" && !d.remote.length) out.push(T.problems.remote);
  if (direction === "out" && d.who === "local" && !d.local.length) out.push(T.problems.local);
  if (d.when === "scheduled" && d.from === d.to) out.push(T.problems.sameTimes);
  if (Array.from(d.name.trim()).length > 48) out.push(T.problems.name);
  return out;
}

/* Whether a row blocks everything every device starts, always. */
export const blocksEverything = (d, direction) => direction === "out" && d.action === "block" && d.type === "all" &&
  d.who === "all" && d.where === "any" && d.when === "always";

/* A rule written the one way, to tell a change from none. */
const canon = (r) => JSON.stringify([
  r.name || "", r.action, r.protocol || "", (r.ports || []).map(String), r.remote || [], r.local || [], r.group || "",
  r.schedule ? [r.schedule.days || [], r.schedule.from, r.schedule.to] : null,
]);

/* What saving the rows would change, against the rules the box holds. */
export function changesOf(before, drafts, direction) {
  const after = drafts.map((d) => ruleOf(d, direction));
  const was = new Map(before.map((r) => [r.id, r]));
  const kept = new Set(after.map((r) => r.id).filter(Boolean));
  const added = after.filter((r) => !r.id);
  const changed = after.filter((r) => r.id && was.has(r.id) && canon(was.get(r.id)) !== canon(r));
  const deleted = before.filter((r) => !kept.has(r.id));
  const moved = before.filter((r) => kept.has(r.id)).map((r) => r.id).join() !== after.filter((r) => r.id).map((r) => r.id).join();
  return { added, changed, deleted, moved, any: added.length + changed.length + deleted.length > 0 || moved };
}

/* What a type reads as in the Protocol and Port range columns. */
const typeProtocol = (t) => (t.protocol ? t.protocol.toUpperCase() : t.custom || t.ports.length ? "TCP and UDP" : "All");

function Action({ action }) {
  return <span className={`fw-sg-action is-${action}`}>{action === "allow" ? "Allow" : "Block"}</span>;
}

function Places({ list, anywhere }) {
  if (!list || !list.length) return <span className="fw-sg-any">{anywhere}</span>;
  return <span className="fw-sg-places">{list.map((p) => <code key={p}>{W.shown(p)}</code>)}</span>;
}

/* The rows every box has coming in, judged before the rules: its own network,
   and the Weavian cloud over the tunnel. box is what the firewall reports of
   itself: tunnel, cloud_address, ports. */
function fixedIn(box) {
  const ports = W.portsLabel(box.ports);
  const rows = [{ key: "lan", type: "All traffic", protocol: "All", ports: "All", source: T.fixed.lan.source, desc: T.fixed.lan.desc }];
  if (box.tunnel) {
    rows.push({
      key: "cloud", type: T.fixed.management.type, protocol: "TCP", ports,
      source: box.cloud_address ? `${T.fixed.cloud.source} (${box.cloud_address})` : T.fixed.cloud.source, desc: T.fixed.cloud.desc,
    });
  }
  return rows;
}

/* A row as the box has it, the same in both modes: read only, locked. */
function FixedCells({ row, cell }) {
  return (
    <>
      {cell(T.cols.n, <FiLock size={13} aria-label={T.fixed.locked} title={T.fixed.locked} />, "fw-sg-n")}
      {cell(T.cols.id, <span className="fw-sg-builtin">{row.id || T.fixed.builtIn}</span>, "fw-sg-id")}
      {cell(T.cols.action, <Action action="allow" />)}
      {cell(T.cols.type, row.type)}
      {cell(T.cols.protocol, row.protocol)}
      {cell(T.cols.ports, row.ports)}
      {cell(T.cols.source, row.source, "is-wide")}
      {cell(T.cols.when, T.always)}
      {cell(T.cols.desc, row.desc, "is-wide")}
    </>
  );
}

const viewCell = (label, content) => <td data-label={label}>{content}</td>;

/* The rules of one direction, to read. */
export function RulesView({ direction, rules, groups, stopped, locked, onEdit, box }) {
  const [filter, setFilter] = useState("");
  const groupName = (id) => groups.find((g) => g.id === id)?.name || id;
  const rows = rules.map((r, i) => {
    const type = W.ruleType(r);
    const devices = r.group ? groupName(r.group) : (r.local || []).map(W.shown).join(", ") || T.everyDevice;
    const text = [i + 1, r.id, r.action, type.label, r.name, devices, (r.remote || []).join(" "), (r.ports || []).join(" "), W.whenLabel(r.schedule)];
    return { r, n: i + 1, type, devices, text: text.join(" ").toLowerCase() };
  });
  const shown = filter.trim() ? rows.filter((x) => x.text.includes(filter.trim().toLowerCase())) : rows;
  const out = direction === "out";
  return (
    <>
      <div className="fw-sg-head">
        <div>
          <h4>{T.tabs[direction]} <span>({rules.length})</span></h4>
          <p>{T.means[direction]}</p>
        </div>
        <div className="fw-sg-tools">
          <label className="fw-sg-filter">
            <FiSearch size={14} aria-hidden="true" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={T.filter} aria-label={T.filter} />
          </label>
          <button type="button" className="fw-btn" disabled={locked} onClick={onEdit}>{T.edit[direction]}</button>
        </div>
      </div>
      <div className="fw-sg-wrap">
        <table className="fw-sg-table">
          <thead>
            <tr>
              <th>{T.cols.n}</th><th>{T.cols.id}</th><th>{T.cols.action}</th><th>{T.cols.type}</th><th>{T.cols.protocol}</th>
              <th>{T.cols.ports}</th>{out && <th>{T.cols.devices}</th>}<th>{out ? T.cols.dest : T.cols.source}</th>
              <th>{T.cols.when}</th><th>{T.cols.desc}</th><th className="is-num">{T.cols.stopped}</th>
            </tr>
          </thead>
          <tbody>
            {!out && fixedIn(box).map((row) => (
              <tr key={row.key} className="is-fixed"><FixedCells row={row} cell={viewCell} /><td data-label={T.cols.stopped} /></tr>
            ))}
            {shown.map(({ r, n, type, devices }) => (
              <tr key={r.id}>
                <td data-label={T.cols.n} className="is-num">{n}</td>
                <td data-label={T.cols.id} className="is-tight"><code>rule-{r.id}</code></td>
                <td data-label={T.cols.action}><Action action={r.action} /></td>
                <td data-label={T.cols.type} className="is-tight">{type.label}</td>
                <td data-label={T.cols.protocol} className="is-tight">{W.protocolLabel(r.protocol, r.ports)}</td>
                <td data-label={T.cols.ports}>{W.portsLabel(r.ports)}</td>
                {out && <td data-label={T.cols.devices}>{devices}</td>}
                <td data-label={out ? T.cols.dest : T.cols.source}><Places list={r.remote} anywhere={T.anywhere} /></td>
                <td data-label={T.cols.when}>{W.whenLabel(r.schedule)}</td>
                <td data-label={T.cols.desc}>{r.name}</td>
                <td data-label={T.cols.stopped} className="is-num">{r.action === "block" ? W.count(stopped[r.id] || 0) : ""}</td>
              </tr>
            ))}
            {rules.length === 0 && (
              <tr className="is-empty"><td colSpan={out ? 11 : 10}>{T.none[direction]}</td></tr>
            )}
            {rules.length > 0 && shown.length === 0 && (
              <tr className="is-empty"><td colSpan={out ? 11 : 10}>{T.noMatch}</td></tr>
            )}
            {!out && (
              <tr className="is-fixed">
                <FixedCells cell={viewCell} row={{
                  type: T.fixed.management.type, protocol: "TCP", ports: W.portsLabel(box.ports),
                  source: (box.management_networks || []).length ? <Places list={box.management_networks} /> : T.fixed.management.none,
                  desc: T.fixed.management.desc,
                }} />
                <td data-label={T.cols.stopped} />
              </tr>
            )}
            <tr className="is-default">
              <td data-label={T.cols.n} className="is-num">*</td>
              <td data-label={T.cols.id} />
              <td data-label={T.cols.action}><Action action={T.defaultAction[direction]} /></td>
              <td data-label={T.cols.type}>All traffic</td>
              <td data-label={T.cols.protocol}>All</td>
              <td data-label={T.cols.ports}>All</td>
              {out && <td data-label={T.cols.devices}>{T.everyDevice}</td>}
              <td data-label={out ? T.cols.dest : T.cols.source}><Places list={[]} anywhere={T.anywhere} /></td>
              <td data-label={T.cols.when}>{T.always}</td>
              <td data-label={T.cols.desc}>{T.everything[direction]}</td>
              <td data-label={T.cols.stopped} />
            </tr>
          </tbody>
        </table>
      </div>
      <p className="fw-muted">{T.order[direction]}</p>
    </>
  );
}

const editCell = (label, content, className) => (
  <div role="cell" data-label={label} className={className}>{content}</div>
);

/* What saving would do, each rule as its sentence. */
function Changes({ changes, management, groupName }) {
  const said = (r) => W.ruleSentence(r, groupName(r.group));
  return (
    <section className="fw-sg-changes" aria-live="polite">
      <h4>{T.changes.title}</h4>
      {!changes.any && !management && <p className="fw-muted">{T.changes.none}</p>}
      <ul>
        {changes.added.map((r, i) => <li key={`a${i}`} className="is-added"><b>{T.changes.added}</b> {said(r)}</li>)}
        {changes.changed.map((r) => <li key={`c${r.id}`} className="is-changed"><b>{T.changes.changed}</b> {r.name}: {said(r)}</li>)}
        {changes.deleted.map((r) => <li key={`d${r.id}`} className="is-deleted"><b>{T.changes.deleted}</b> {r.name}: {said(r)}</li>)}
        {changes.moved && <li className="is-moved">{T.changes.moved}</li>}
        {management && <li className="is-changed">{T.changes.management(management.added, management.removed)}</li>}
      </ul>
    </section>
  );
}

/* The rules of one direction, every one a row to change, saved together. */
export function RulesEditor({ direction, rules, groups, boxTime, locked, onCancel, onSave, box }) {
  const [drafts, setDrafts] = useState(() => rules.map(draftOf));
  const [management, setManagement] = useState(() => box.management_networks || []);
  const [preview, setPreview] = useState(false);
  const [tried, setTried] = useState(false);
  const out = direction === "out";
  const groupName = (id) => groups.find((g) => g.id === id)?.name || id;
  const set = (key, patch) => setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  const move = (i, by) => setDrafts((ds) => {
    const next = [...ds];
    const [d] = next.splice(i, 1);
    next.splice(i + by, 0, d);
    return next;
  });
  const problems = drafts.map((d) => rowProblems(d, direction));
  const broken = problems.some((p) => p.length > 0);
  const changes = changesOf(rules, drafts, direction);
  const was = box.management_networks || [];
  const managed = direction === "in" && {
    added: management.filter((n) => !was.includes(n)),
    removed: was.filter((n) => !management.includes(n)),
  };
  const managementChange = managed && (managed.added.length || managed.removed.length) ? managed : null;
  const anywhereIn = !out && drafts.some((d) => d.action === "allow" && d.where === "any");
  const everything = drafts.some((d) => blocksEverything(d, direction));
  const save = () => {
    setTried(true);
    if (!broken) onSave(drafts.map((d) => ruleOf(d, direction)), everything, out ? undefined : management);
  };
  const cols = [T.cols.n, T.cols.id, T.cols.action, T.cols.type, T.cols.protocol, T.cols.ports,
    ...(out ? [T.cols.devices] : []), out ? T.cols.dest : T.cols.source, T.cols.when, T.cols.desc, ""];
  return (
    <div className="fw-sg-editor">
      <div className="fw-sg-head">
        <div>
          <h4>{T.edit[direction]}</h4>
          <p>{T.means[direction]}</p>
        </div>
      </div>
      <div className="fw-sg-wrap">
        <div className={`fw-sg-grid is-${direction}`} role="table" aria-label={T.edit[direction]}>
          <div className="fw-sg-row is-head" role="row">
            {cols.map((c, i) => <span key={i} role="columnheader">{c}</span>)}
          </div>
          {!out && fixedIn(box).map((row) => (
            <div key={row.key} className="fw-sg-row is-fixed" role="row"><FixedCells row={row} cell={editCell} /><div role="cell" /></div>
          ))}
          {drafts.map((d, i) => {
            const type = typeOf(d.type);
            const n = i + 1;
            const bad = tried && problems[i].length > 0;
            return (
              <div key={d.key} className={`fw-sg-row${bad ? " is-bad" : ""}`} role="row">
                <div role="cell" data-label={T.cols.n} className="fw-sg-n">
                  <span>{n}</span>
                  <span className="fw-sg-move">
                    <button type="button" aria-label={T.up(n)} disabled={i === 0} onClick={() => move(i, -1)}><FiArrowUp size={13} /></button>
                    <button type="button" aria-label={T.down(n)} disabled={i === drafts.length - 1} onClick={() => move(i, 1)}><FiArrowDown size={13} /></button>
                  </span>
                </div>
                <div role="cell" data-label={T.cols.id} className="fw-sg-id">{d.id ? <code>rule-{d.id}</code> : <em>{T.newRule}</em>}</div>
                <div role="cell" data-label={T.cols.action}>
                  <select className="fw-input" value={d.action} aria-label={`Rule ${n} ${T.cols.action}`} onChange={(e) => set(d.key, { action: e.target.value })}>
                    <option value="block">Block</option>
                    <option value="allow">Allow</option>
                  </select>
                </div>
                <div role="cell" data-label={T.cols.type}>
                  <select className="fw-input" value={d.type} aria-label={`Rule ${n} ${T.cols.type}`} onChange={(e) => set(d.key, { type: e.target.value })}>
                    {W.RULE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div role="cell" data-label={T.cols.protocol}>
                  <input className="fw-input" disabled value={typeProtocol(type)} aria-label={`Rule ${n} ${T.cols.protocol}`} />
                </div>
                <div role="cell" data-label={T.cols.ports}>
                  {type.custom ? (
                    <input className="fw-input" value={d.ports} placeholder="e.g. 8000-8100" aria-label={`Rule ${n} ${T.cols.ports}`}
                      onChange={(e) => set(d.key, { ports: e.target.value })} />
                  ) : (
                    <input className="fw-input" disabled value={W.portsLabel(type.ports)} aria-label={`Rule ${n} ${T.cols.ports}`} />
                  )}
                </div>
                {out && (
                  <div role="cell" data-label={T.cols.devices} className="is-wide">
                    <select className="fw-input" value={d.who} aria-label={`Rule ${n} ${T.cols.devices}`} onChange={(e) => set(d.key, { who: e.target.value })}>
                      <option value="all">{T.everyDevice}</option>
                      {groups.map((g) => <option key={g.id} value={`group:${g.id}`}>{g.name}</option>)}
                      <option value="local">{T.someAddresses}</option>
                    </select>
                    {d.who === "local" && (
                      <AddressChips values={d.local} onChange={(local) => set(d.key, { local })} placeholder={T.deviceSearch}
                        label={`Rule ${n} device address`} disabled={locked} />
                    )}
                  </div>
                )}
                <div role="cell" data-label={out ? T.cols.dest : T.cols.source} className="is-wide">
                  <select className="fw-input" value={d.where} aria-label={`Rule ${n} ${out ? T.cols.dest : T.cols.source}`}
                    onChange={(e) => set(d.key, { where: e.target.value })}>
                    <option value="any">{T.anywhere}</option>
                    <option value="custom">{T.custom}</option>
                  </select>
                  {d.where === "custom" && (
                    <AddressChips values={d.remote} onChange={(remote) => set(d.key, { remote })} placeholder={T.placeSearch}
                      label={`Rule ${n} ${out ? "destination" : "source"} address`} disabled={locked} />
                  )}
                </div>
                <div role="cell" data-label={T.cols.when}>
                  <select className="fw-input" value={d.when} aria-label={`Rule ${n} ${T.cols.when}`} onChange={(e) => set(d.key, { when: e.target.value })}>
                    <option value="always">{T.always}</option>
                    <option value="scheduled">{T.scheduled}</option>
                  </select>
                </div>
                <div role="cell" data-label={T.cols.desc} className="is-wide">
                  <input className="fw-input" value={d.name} placeholder={T.descPlaceholder} aria-label={`Rule ${n} ${T.cols.desc}`}
                    onChange={(e) => set(d.key, { name: e.target.value })} />
                </div>
                <div role="cell" className="fw-sg-del">
                  <button type="button" className="fw-btn" aria-label={`${T.remove} rule ${n}`}
                    onClick={() => setDrafts((ds) => ds.filter((x) => x.key !== d.key))}>{T.remove}</button>
                </div>
                {d.when === "scheduled" && (
                  <div className="fw-sg-sub">
                    <span className="fw-days">
                      {T.days.map(([day, short]) => (
                        <button key={day} type="button" aria-pressed={d.days.includes(day)} className={`fw-pill${d.days.includes(day) ? " is-on" : ""}`}
                          onClick={() => set(d.key, { days: d.days.includes(day) ? d.days.filter((x) => x !== day) : [...d.days, day] })}>{short}</button>
                      ))}
                    </span>
                    <label>{T.from} <input type="time" value={d.from} aria-label={`Rule ${n} from`} onChange={(e) => set(d.key, { from: e.target.value })} /></label>
                    <label>{T.to} <input type="time" value={d.to} aria-label={`Rule ${n} to`} onChange={(e) => set(d.key, { to: e.target.value })} /></label>
                    <small>{T.boxTime(boxTime)}</small>
                  </div>
                )}
                {bad && <div className="fw-sg-sub is-bad" role="alert">{problems[i].join(" ")}</div>}
              </div>
            );
          })}
          {drafts.length === 0 && <div className="fw-sg-row is-empty">{T.none[direction]}</div>}
          {!out && (
            <div className="fw-sg-row is-fixed" role="row">
              <FixedCells cell={editCell} row={{
                type: T.fixed.management.type, protocol: "TCP", ports: W.portsLabel(box.ports),
                source: <AddressChips values={management} onChange={setManagement} placeholder={T.fixed.management.search}
                  label={T.fixed.management.label} disabled={locked} />,
                desc: T.fixed.management.desc,
              }} />
              <div role="cell" />
              <p className="fw-sg-sub fw-sg-note">{T.managementSafe}</p>
            </div>
          )}
        </div>
      </div>
      <button type="button" className="fw-btn fw-sg-add" onClick={() => setDrafts((ds) => [...ds, newDraft(direction)])}>
        <FiPlus size={14} aria-hidden="true" /> {T.add}
      </button>
      {anywhereIn && <div className="fw-notice is-warn" role="note"><FiAlertTriangle size={16} /> <span>{T.warnAnywhereIn}</span></div>}
      {everything && <div className="fw-notice is-warn" role="note"><FiAlertTriangle size={16} /> <span>{T.warnEverything}</span></div>}
      {tried && broken && <div className="fw-notice is-bad" role="alert"><FiAlertTriangle size={16} /> <span>{T.fixFirst}</span></div>}
      {preview && <Changes changes={changes} management={managementChange} groupName={groupName} />}
      <footer className="fw-sg-foot">
        <button type="button" className="fw-btn is-quiet" onClick={onCancel}>{T.cancel}</button>
        <button type="button" className="fw-btn" onClick={() => setPreview(!preview)}>{preview ? T.hidePreview : T.preview}</button>
        <button type="button" className="fw-btn is-primary" disabled={locked || (!changes.any && !managementChange)} onClick={save}>{T.save}</button>
      </footer>
    </div>
  );
}
